import { styled, Theme } from '@nexelem/deprecated-theme';
import { ProLightCheck } from '@nexelem/icons/ProLightCheck';
import { ProLightEllipsisVertical } from '@nexelem/icons/ProLightEllipsisVertical';
import { IconButton } from '@nexelem/components';
import { FC, ReactNode, SyntheticEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons';

const StyledListItem = styled('li')<{ isSelected: boolean }>(({ theme, isSelected }) => {
  const typedTheme = theme as Theme;
  const { getColor, hexToRGB } = typedTheme.palette;
  const { multipleActionsListItem } = typedTheme.client.lists;
  // eslint-disable-next-line max-len
  const backgroundColor = isSelected
    ? hexToRGB(getColor(multipleActionsListItem.selected.backgroundColor), 0.9)
    : getColor(multipleActionsListItem.defaultState.backgroundColor);
  return {
    alignItems: 'center',
    backgroundColor,
    border: `1px solid ${getColor(multipleActionsListItem.defaultState.borderColor)}`,
    borderRadius: typedTheme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: typedTheme.spacing(2, 1, 2, 4),
    '.filter-actions': {
      display: 'flex',
      alignItems: 'center',
      button: {
        '&:first-child': {
          marginRight: '6px',
        },
        borderRadius: '50%',
        '&:hover': {
          backgroundColor: hexToRGB(getColor(multipleActionsListItem.hover.backgroundColor), 0.9),
        },
      },
    },
    '.container': {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      '.primary': {
        display: 'flex',
        '.text': {
          ...typedTheme.typography.sourceSansPro16Regular,
          color: getColor(multipleActionsListItem.defaultState.primaryTextColor),
          lineHeight: '24px',
        },
      },
      '.secondary': {
        ...typedTheme.typography.titilliumWebSemiBold12,
        color: getColor(multipleActionsListItem.defaultState.secondaryTextColor),
      },
      '.checkmark': {
        alignItems: 'center',
        backgroundColor: getColor(multipleActionsListItem.selected.checkmark.backgroundColor),
        borderRadius: typedTheme.spacing(3),
        color: getColor(multipleActionsListItem.selected.checkmark.color),
        display: 'flex',
        justifyContent: 'center',
        marginRight: typedTheme.spacing(2),
        padding: typedTheme.spacing(0.5, 2),
        svg: {
          height: '20px',
        },
      },
    },
  };
});

export type MultipleActionsItemListProps = {
  readonly isSelected: boolean;
  readonly primaryText: ReactNode;
  readonly secondaryText: string;
  onSelect: (evt: SyntheticEvent) => void;
  onClickMore?: (evt: SyntheticEvent) => void;
  ActionButton?: FC;
  withCheckmark?: boolean;
};

export function MultipleActionsItemList({
  isSelected,
  primaryText,
  secondaryText,
  onClickMore,
  onSelect,
  withCheckmark,
  ActionButton = () => <IconButton Icon={ProLightEllipsisVertical} onClick={onClickMore} />,
}: MultipleActionsItemListProps) {
  return (
    <StyledListItem isSelected={isSelected}>
      <div className="container">
        <div className="primary">
          {isSelected && withCheckmark && (
            <span className="checkmark">
              <FontAwesomeIcon icon={faCheck} />
            </span>
          )}
          <div className="text">{primaryText}</div>
        </div>
        <div className="secondary">{secondaryText}</div>
      </div>
      <div className="filter-actions">
        <ActionButton />
        {isSelected ? <div className="w-11" /> : <IconButton Icon={ProLightCheck} size="small" onClick={onSelect} />}
      </div>
    </StyledListItem>
  );
}
